import React from "react";
import "./TasksTabPanel.css";
import PropTypes from 'prop-types';
import { Box, CircularProgress, TextField, InputAdornment } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import MenuListTasks from "../../Components/MenuListTasks/MenuListTasks";
import useAxios from "../../Hooks/useAxios";
import { GlobalContext } from "../../Context/GlobalContext";
import ActiveProcess from "../../Components/ActiveProcess/ActiveProcess";
import { useActiveProcesses } from "../../Components/ActiveProcess/ActiveProcess";
import { STATUS_RESOLVED } from "../../Constants/Global";

/**
 * A tab panel component for displaying tasks (subprocesses) in a tabbed interface. It fetches task data from an API
 * and renders the tasks using the `MenuListTasks` component. The component is designed to work within a tabbed layout.
 * * @component
 * @example
 * <TasksTabPanel value={currentTab} index={0} tabType="myTasks" />
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The children of the tab panel.
 * @param {number} props.value - The current value of the selected tab.
 * @param {number} props.index - The index of this tab panel.
 * @param {string} props.tabType - The type of tasks to display (e.g., "myTasks" or "allTasks").
 * @returns {JSX.Element} The rendered tab panel component.
 */
function TasksTabPanel(props) {
  const { children, value, index, tabType, ...other } = props;
  const { data, isLoading, error } = useAxios(tabType);
  const { setMyTasks, setAllTasks, setAlertBarSeverity, setAlertBarMessage, setAlertBarOpen } = React.useContext(GlobalContext);
  const [subprocesses, setSubprocesses] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");



  /**
   * Fetches task data from the API and updates the state. Displays an error message if the request fails.
   */
  React.useEffect(() => {
    if (!isLoading && data) {
      if (error){
        setMyTasks([]);
        setAllTasks([]);
        setAlertBarSeverity("error");
        setAlertBarMessage(error);
        setAlertBarOpen(true);
      } else {
        setSubprocesses(data.filter(item => item.type === "subprocess"));
        if (tabType === "myTasks") {
          setMyTasks(data);
        } else {
          setAllTasks(data);
        }
      }
    }
  }, [isLoading, data]);


  let activeProcesses = useActiveProcesses();




  //$$ This function compares subprocess labels with active projects Id
  //const BelongsToActiveProject = (label, array) => {
  //  return array.includes(label)
  //};


  /**
   * Handles the search input change.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The input change event.
   */
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  /**
   * Renders the list of tasks (subprocesses) filtered by process_id or employee name.
   * * @returns {Array|JSX.Element} An array of `MenuListTasks` components or loading/error state.
   */
  const displayTasks = () => {

    if (isLoading){
      return <><CircularProgress size="1rem" sx={{marginRight: "4px"}} />Loading...</>;
    } else if (error){
      return error;
    }



    // filter by procesId <= employee_process.id || targeted_employee.getTargetEmployeeFullName
    const filteredTasks = subprocesses.filter(item => {



      const processId = item.label ? String(item.label).toLowerCase() : "";
      const employeeName = item.employeeName ? item.employeeName.toLowerCase() : "";

      //// Filtering by activeProcess
      //return BelongsToActiveProject(item.label, activeProcesses) &&
      //(
      //  processId.includes(searchTerm) ||
      //  employeeName.includes(searchTerm)
      //);

      //// Filtering by activeSubProcess
      return item.statusId !== STATUS_RESOLVED
        && (processId.includes(searchTerm)
            || employeeName.includes(searchTerm));
    });

    return filteredTasks.map(item => (
      <MenuListTasks item={item} tagType="taskList" key={tabType + item.id}/>
    ));
  };

  if (value !== index) {
    return null;
  }

  return (
    <Box
      role="tabpanel"
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      sx={{
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "78vh",
      }}
      {...other}
    >
      <Box sx={{ p: '16px 16px 0px 16px', flexGrow: 1, overflowY: "auto" }}>
        {displayTasks()}
      </Box>

      <Box
        sx={{
          p: '12px 16px 16px 16px',
          backgroundColor: "#ffffff",
          zIndex: 1,
          borderBottomLeftRadius: '24px',
          borderBottomRightRadius: '24px',
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="emp_pro.id || emp.name"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            sx: {
              borderRadius: "20px",
              backgroundColor: "#fff"
            }
          }}
        />
      </Box>
    </Box>
  );
}

TasksTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

export default TasksTabPanel;